import { Box } from "@mui/material";
import { Ruler } from "@procore/core-icons";
import { useCallback, useEffect, useMemo } from "react";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { store } from "app/store";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars, useToolbarButtons } from "contexts/toolbars";
import { getBestAxisAlignedView } from "features/orthoCam/utils";
import {
    CameraType,
    renderActions,
    selectCameraType,
    selectDefaultVisibility,
    selectIsXRayActive,
} from "features/render";
import { useEnterToolMode } from "features/toolMode/useEnterToolMode";
import { undoRedoActions } from "features/undoRedo";
import OrthographicIcon from "media/icons/orthographic.svg?react";
import SectionIcon from "media/icons/section.svg?react";
import XRayIcon from "media/icons/xray.svg?react";
import { ProcoreToolMode } from "slices/procoreUx";
import { DEFAULT_MEASURE_PICKER, DEFAULT_SECTION_PICKER } from "slices/procoreUx/utils";
import { telemetry } from "telemetry";

import { PROCORE_BOTTOM_ENTRY } from "../constants";
import { BottomToolbarButton } from "./BottomToolbarButton";

interface ToolsButtonRendererProps {
    toolbarId: string;
}

/**
 * Custom button renderer for tools content buttons
 */
function ToolsButtonRenderer({ toolbarId }: ToolsButtonRendererProps) {
    const buttons = useToolbarButtons(toolbarId);

    if (buttons.length === 0) {
        return null;
    }

    return (
        <Box display="flex" gap={`${PROCORE_BOTTOM_ENTRY.GAP}px`}>
            {buttons.map((buttonEntry) => {
                const { config, state } = buttonEntry;

                if (!state.visible) {
                    return null;
                }

                return (
                    <BottomToolbarButton
                        key={config.id}
                        icon={config.icon}
                        label={config.title || ""}
                        tooltip={config.tooltip?.toolName || config.title || ""}
                        active={state.active}
                        onClick={config.onClick}
                    />
                );
            })}
        </Box>
    );
}

/**
 * Content for the Tools tab - activates tools
 */
export function BottomToolbarToolsContent() {
    const toolbarDispatch = useDispatchToolbars();
    const dispatch = useAppDispatch();
    const enterToolMode = useEnterToolMode();
    const cameraType = useAppSelector(selectCameraType);
    const isXRayActive = useAppSelector(selectIsXRayActive);
    const {
        state: { view, scene },
    } = useExplorerGlobals(true);

    const handleXRayToggle = useCallback(() => {
        const currentVisibility = selectDefaultVisibility(store.getState());
        dispatch(renderActions.toggleXRayMode());
        dispatch(
            undoRedoActions.push({
                description: "Toggle X-Ray mode",
                undoCallback: () => dispatch(renderActions.setVisibilityMode(currentVisibility)),
                redoCallback: () => dispatch(renderActions.toggleXRayMode()),
            }),
        );
    }, [dispatch]);

    const handleCameraToggle = useCallback(() => {
        if (!view || !scene) {
            return;
        }
        void track(Events.toolbar.cameraTapped, { origin: EventOrigin.Toolbar });

        if (cameraType === CameraType.Pinhole) {
            getBestAxisAlignedView(view)
                .then((orthographicParams) => {
                    dispatch(
                        renderActions.setCamera({
                            type: CameraType.Orthographic,
                            goTo: orthographicParams,
                        }),
                    );
                })
                .catch((error) => {
                    console.warn("Failed to calculate best axis-aligned view:", error);
                    telemetry.trackCaughtException({
                        source: "BottomToolbarToolsContent.cameraToggle",
                        category: "render",
                        error,
                    });
                    dispatch(renderActions.setCamera({ type: CameraType.Orthographic }));
                });
        } else {
            dispatch(renderActions.setCamera({ type: CameraType.Pinhole }));
        }
    }, [dispatch, view, scene, cameraType]);

    const buttons: ToolbarButtonEntry[] = useMemo(
        () => [
            // {
            //     config: {
            //         id: ProcoreToolMode.Markup,
            //         icon: <Brush />,
            //         onClick: () => dispatch(procoreUxActions.setActiveToolMode(ProcoreToolMode.Markup)),
            //         title: "Markup",
            //         tooltip: {
            //             toolName: "Markup tools",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            // {
            //     config: {
            //         id: ProcoreToolMode.Create,
            //         icon: <Add />,
            //         onClick: () => dispatch(procoreUxActions.setActiveToolMode(ProcoreToolMode.Create)),
            //         title: "Create",
            //         tooltip: {
            //             toolName: "Create objects",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            {
                config: {
                    id: ProcoreToolMode.Measure,
                    icon: <Ruler />,
                    onClick: () => {
                        enterToolMode({ picker: DEFAULT_MEASURE_PICKER });
                        void track(Events.toolbar.measureTapped, { origin: EventOrigin.Toolbar });
                    },
                    title: "Measure",
                    tooltip: {
                        toolName: "Measurement tools",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: ProcoreToolMode.Section,
                    icon: <SectionIcon />,
                    onClick: () => {
                        enterToolMode({ picker: DEFAULT_SECTION_PICKER });
                        void track(Events.toolbar.sectionTapped, { origin: EventOrigin.Toolbar });
                    },
                    title: "Section",
                    tooltip: {
                        toolName: "Section tools",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: "camera-toggle",
                    icon: <OrthographicIcon />,
                    onClick: handleCameraToggle,
                    title: "Ortho",
                    tooltip: {
                        toolName: "Orthographic camera",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: cameraType === CameraType.Orthographic,
                    disabled: !view || !scene,
                    visible: false,
                },
            },
            {
                config: {
                    id: "xray-toggle",
                    icon: <XRayIcon />,
                    onClick: handleXRayToggle,
                    title: "X-Ray",
                    tooltip: {
                        toolName: "X-Ray mode",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: isXRayActive,
                    disabled: false,
                    visible: true,
                },
            },
        ],
        [cameraType, view, scene, handleCameraToggle, isXRayActive, handleXRayToggle, enterToolMode],
    );

    const toolbarId = "bottom-toolbar-tools";

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id: toolbarId,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(toolbarId));
        };
    }, [toolbarDispatch, buttons]);

    return <ToolsButtonRenderer toolbarId={toolbarId} />;
}
